import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { AuthContext } from '../context/AuthContext';

// Animation variants for sections
const sectionVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

// Animation for notifications
const notificationVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } },
};

// Animation for loading icon
const loadingIconVariants = {
    animate: {
        scale: [1, 1.2, 1],
        rotate: [0, 360],
        transition: {
            scale: { repeat: Infinity, duration: 1.5, ease: 'easeInOut' },
            rotate: { repeat: Infinity, duration: 1, ease: 'linear' },
        },
    },
};

// Animation for submitting icon
const submittingIconVariants = {
    initial: { opacity: 0, scale: 0 },
    animate: {
        opacity: 1,
        scale: 1,
        transition: { duration: 0.3, ease: 'easeOut' },
    },
    exit: { opacity: 0, scale: 0, transition: { duration: 0.2 } },
};

const ResultPage = () => {
    const { submissionId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [reportLink, setReportLink] = useState(null);
    const [aiInsights, setAiInsights] = useState(location.state?.refinedInsights || null); // Changed to refinedInsights
    const [loading, setLoading] = useState({ download: false, view: false, back: false, initial: true });
    const [errorMessage, setErrorMessage] = useState('');
    const { token } = useContext(AuthContext);

    useEffect(() => {
        console.log("Submission ID from URL:", submissionId);
        console.log("Token from AuthContext:", token);
        if (!submissionId) {
            console.error("Submission ID is undefined. Check route configuration.");
            setErrorMessage('Invalid submission ID. Please try again.');
            setTimeout(() => {
                setErrorMessage('');
                navigate('/intakeform');
            }, 3000);
            setLoading({ ...loading, initial: false });
            return;
        }

        if (!token) {
            console.error("No token found. Redirecting to login.");
            setErrorMessage('You need to be logged in to view this page. Redirecting to login...');
            setTimeout(() => {
                setErrorMessage('');
                navigate('/login');
            }, 3000);
            setLoading({ ...loading, initial: false });
            return;
        }

        const fetchData = async () => {
            setErrorMessage('');
            setLoading({ ...loading, initial: true });

            try {
                // Fetch submission data to get refinedInsights if not already in state
                if (!aiInsights) {
                    console.log(`Fetching submission data for ID: ${submissionId}`);
                    const submissionResponse = await axios.get(
                        `${process.env.REACT_APP_API_URL}/api/submission/${submissionId}`, // Changed to /api/submission
                        {
                            headers: {
                                'Authorization': `Bearer ${token}`,
                            },
                            timeout: 10000,
                        }
                    );
                    console.log('Submission Response:', submissionResponse.data);
                    setAiInsights(submissionResponse.data.refinedInsights); // Use refinedInsights
                }

                // Fetch report link
                const reportResponse = await axios.get(
                    `${process.env.REACT_APP_API_URL}/api/generateReport/${submissionId}`,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                        },
                        timeout: 10000,
                    }
                );
                console.log('Report Link Response:', reportResponse.data);
                setReportLink(reportResponse.data.downloadLink);
            } catch (error) {
                console.error('Fetch Data Failed:', error);
                let message = 'Failed to fetch report data. Please try again.';
                if (error.response && (error.response.status === 401 || error.response.status === 403)) {
                    message = 'You need to be logged in to view this page. Please log in and try again.';
                    navigate('/login');
                } else if (error.response && error.response.status === 404) {
                    message = 'Submission not found. Redirecting to form...';
                    setTimeout(() => navigate('/intakeform'), 3000);
                }
                setErrorMessage(message);
                setTimeout(() => setErrorMessage(''), 3000);
            } finally {
                setLoading({ ...loading, initial: false });
            }
        };

        fetchData();
    }, [submissionId, navigate, token, aiInsights]);

    const handleDownloadReport = async () => {
        if (!reportLink) {
            setErrorMessage('Report link not available. Please try again.');
            setTimeout(() => setErrorMessage(''), 3000);
            return;
        }
        setLoading({ ...loading, download: true });
        setErrorMessage('');
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}${reportLink}`,
                {
                    responseType: 'blob',
                    timeout: 10000,
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                }
            );

            const blob = new Blob([response.data], { type: response.headers['content-type'] });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `AI_Insights_Report_${submissionId}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Report Download Failed:', error);
            setErrorMessage('Failed to download report. Please try again.');
            setTimeout(() => setErrorMessage(''), 3000);
        } finally {
            setLoading({ ...loading, download: false });
        }
    };

    const handleViewReport = async () => {
        if (!reportLink) {
            setErrorMessage('Report link not available. Please try again.');
            setTimeout(() => setErrorMessage(''), 3000);
            return;
        }
        setLoading({ ...loading, view: true });
        setErrorMessage('');
        try {
            const fullUrl = `${process.env.REACT_APP_API_URL}${reportLink}`;
            const response = await axios.get(fullUrl, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
                responseType: 'blob',
            });

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const blobUrl = window.URL.createObjectURL(blob);
            window.open(blobUrl, '_blank', 'noopener,noreferrer');
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error('Report View Failed:', error);
            setErrorMessage('Failed to view report. Please try again.');
            setTimeout(() => setErrorMessage(''), 3000);
        } finally {
            setLoading({ ...loading, view: false });
        }
    };

    const handleBackToForm = () => {
        setLoading({ ...loading, back: true });
        setTimeout(() => {
            navigate('/intakeform');
        }, 500);
    };

    const riskAssessment = aiInsights?.riskAndCompliance
        ? [
            {
                level: 'High',
                description: aiInsights.riskAndCompliance.regulatoryConsiderations?.join(', ') || 'N/A',
                mitigation: aiInsights.riskAndCompliance.mitigationStrategies?.join(', ') || 'N/A',
            },
            {
                level: 'Medium',
                description: aiInsights.riskAndCompliance.ethicalConsiderations?.join(', ') || 'N/A',
                mitigation: aiInsights.riskAndCompliance.dataSecurity || 'N/A',
            },
        ]
        : [];

    const implementationSteps = aiInsights?.implementationRoadmap
        ? [
            aiInsights.implementationRoadmap.phase1
                ? `${aiInsights.implementationRoadmap.phase1.focus} (${aiInsights.implementationRoadmap.phase1.timeline})`
                : null,
            aiInsights.implementationRoadmap.phase2
                ? `${aiInsights.implementationRoadmap.phase2.focus} (${aiInsights.implementationRoadmap.phase2.timeline})`
                : null,
            aiInsights.implementationRoadmap.phase3
                ? `${aiInsights.implementationRoadmap.phase3.focus} (${aiInsights.implementationRoadmap.phase3.timeline})`
                : null,
        ].filter(Boolean)
        : [];

    if (loading.initial) {
        return (
            <div className="min-h-screen bg-white text-gray-900 flex flex-col font-sans">
                <Header />
                <div className="flex-grow flex items-center justify-center py-16">
                    <motion.div
                        className="flex items-center space-x-2"
                        variants={loadingIconVariants}
                        animate="animate"
                    >
                        <svg
                            className="h-6 w-6 text-[#2596be]"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                            ></circle>
                            <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                        </svg>
                        <span className="text-gray-600">Loading...</span>
                    </motion.div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white text-gray-900 flex flex-col font-sans">
            <Header />
            {errorMessage && (
                <motion.div
                    className="fixed top-4 right-4 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg z-50"
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    variants={notificationVariants}
                >
                    {errorMessage}
                </motion.div>
            )}
            <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                className="flex-grow flex items-center justify-center mt-20 md:mt-24 lg:mt-28 px-4 py-16 pb-16"
            >
                <motion.div
                    className="max-w-4xl w-full p-6 sm:p-8 bg-white rounded-2xl shadow-md border border-gray-200 min-h-[120vh]"
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                >
                    <h2 className="text-2xl sm:text-3xl font-bold text-center mb-8 bg-clip-text text-transparent bg-gradient-to-r from-cyan-500 to-blue-600">
                        Your Custom AI Roadmap
                    </h2>

                    <motion.section variants={sectionVariants} initial="hidden" animate="visible" className="mb-12">
                        <h3 className="text-lg sm:text-xl font-semibold text-[#2596be] mb-3">Executive Summary</h3>
                        <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
                            {aiInsights?.executiveSummary || 'No executive summary available.'}
                        </p>
                    </motion.section>

                    <motion.section variants={sectionVariants} initial="hidden" animate="visible" className="mb-12">
                        <h3 className="text-lg sm:text-xl font-semibold text-[#2596be] mb-4">Recommended Tools</h3>
                        {aiInsights?.recommendedTools?.length > 0 ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                {aiInsights.recommendedTools.map((tool, index) => (
                                    <div
                                        key={index}
                                        className="flex items-start p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                                    >
                                        <div className="mr-4">
                                            <span
                                                className={`inline-block w-6 h-6 rounded-full ${tool.source === 'Google Cloud' ? 'bg-blue-500' : 'bg-gray-500'
                                                    }`}
                                                title={tool.source}
                                            ></span>
                                        </div>
                                        <div>
                                            <p className="text-gray-900 font-medium">{tool.name || 'Unnamed Tool'}</p>
                                            <p className="text-gray-500 text-sm">{tool.source || 'Unknown Source'}</p>
                                            <p className="text-gray-600 text-sm">{tool.description || 'No description available.'}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-gray-600 text-sm">No recommended tools available.</p>
                        )}
                    </motion.section>

                    <motion.section variants={sectionVariants} initial="hidden" animate="visible" className="mb-12">
                        <h3 className="text-lg sm:text-xl font-semibold text-[#2596be] mb-4">ROI Estimate</h3>
                        <div className="flex flex-col sm:flex-row gap-4">
                            <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full border border-blue-200">
                                Timeframe: {aiInsights?.roiEstimate?.timeframe || 'N/A'}
                            </span>
                            <span className="inline-block px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full border border-green-200">
                                Potential Value: {aiInsights?.roiEstimate?.potentialValue || 'N/A'}
                            </span>
                        </div>
                    </motion.section>

                    <motion.section variants={sectionVariants} initial="hidden" animate="visible" className="mb-12">
                        <h3 className="text-lg sm:text-xl font-semibold text-[#2596be] mb-4">Risk Assessment</h3>
                        {riskAssessment.length > 0 ? (
                            <div className="space-y-4">
                                {riskAssessment.map((risk, index) => (
                                    <div
                                        key={index}
                                        className={`p-4 rounded-lg border ${risk.level === 'High'
                                            ? 'bg-red-50 border-red-200 text-red-800'
                                            : 'bg-orange-50 border-orange-200 text-orange-800'
                                            }`}
                                    >
                                        <p className="font-medium">
                                            {risk.level} Risk: {risk.description}
                                        </p>
                                        <p className="text-sm">Mitigation: {risk.mitigation}</p>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-gray-600 text-sm">No risk assessment available.</p>
                        )}
                    </motion.section>

                    <motion.section variants={sectionVariants} initial="hidden" animate="visible" className="mb-12">
                        <h3 className="text-lg sm:text-xl font-semibold text-[#2596be] mb-4">Implementation Steps</h3>
                        <ol className="list-decimal list-inside text-gray-600 space-y-3 text-sm sm:text-base">
                            {implementationSteps.length > 0 ? (
                                implementationSteps.map((step, index) => (
                                    <li key={index} className="pl-2">
                                        <span className="font-medium text-gray-900">{index + 1}.</span> {step}
                                    </li>
                                ))
                            ) : (
                                <p className="text-gray-600 text-sm">No implementation steps available.</p>
                            )}
                        </ol>
                    </motion.section>

                    <motion.section variants={sectionVariants} initial="hidden" animate="visible" className="mb-12">
                        <h3 className="text-lg sm:text-xl font-semibold text-[#2596be] mb-4">Departmental Applications</h3>
                        {aiInsights?.departmentalApplications ? (
                            <div className="space-y-6">
                                {Object.entries(aiInsights.departmentalApplications).map(([dept, apps]) => (
                                    <div key={dept}>
                                        <h4 className="text-md font-medium capitalize text-gray-900 mb-2">{dept}</h4>
                                        {apps?.length > 0 ? (
                                            <ul className="list-disc list-inside text-gray-600 text-sm space-y-1">
                                                {apps.map((app, index) => (
                                                    <li key={index}>
                                                        {app.use} - {app.benefit}
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <p className="text-gray-600 text-sm">No applications suggested.</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-gray-600 text-sm">No departmental applications available.</p>
                        )}
                    </motion.section>

                    <div className="flex flex-col sm:flex-row gap-4">
                        <motion.button
                            onClick={handleDownloadReport}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            disabled={loading.download || !reportLink}
                            className={`flex-1 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-md font-medium text-white shadow-lg hover:shadow-cyan-500/50 transition-all border border-cyan-500 flex items-center justify-center ${loading.download || !reportLink ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                        >
                            {loading.download ? (
                                <motion.span
                                    className="flex items-center space-x-2"
                                    variants={loadingIconVariants}
                                    animate="animate"
                                >
                                    <svg
                                        className="h-4 w-4 text-white"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        ></circle>
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                        ></path>
                                    </svg>
                                    <span>Downloading...</span>
                                </motion.span>
                            ) : (
                                <span>Download Report</span>
                            )}
                        </motion.button>

                        <motion.button
                            onClick={handleViewReport}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            disabled={loading.view || !reportLink}
                            className={`flex-1 px-6 py-3 bg-gradient-to-r from-green-500 to-teal-600 rounded-md font-medium text-white shadow-lg hover:shadow-green-500/50 transition-all border border-green-500 flex items-center justify-center ${loading.view || !reportLink ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                        >
                            {loading.view ? (
                                <motion.span
                                    className="flex items-center space-x-2"
                                    variants={loadingIconVariants}
                                    animate="animate"
                                >
                                    <svg
                                        className="h-4 w-4 text-white"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        ></circle>
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                        ></path>
                                    </svg>
                                    <span>Opening...</span>
                                </motion.span>
                            ) : (
                                <span>View Report</span>
                            )}
                        </motion.button>

                        <motion.button
                            onClick={handleBackToForm}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            disabled={loading.back}
                            className={`flex-1 px-6 py-3 bg-gray-600 rounded-md font-medium text-white shadow-lg hover:shadow-gray-500/50 transition-all border border-gray-500 flex items-center justify-center ${loading.back ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                        >
                            {loading.back ? (
                                <motion.span
                                    className="flex items-center space-x-2"
                                    variants={loadingIconVariants}
                                    animate="animate"
                                >
                                    <svg
                                        className="h-4 w-4 text-white"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        ></circle>
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                        ></path>
                                    </svg>
                                    <span>Navigating...</span>
                                </motion.span>
                            ) : (
                                <span>Back to Form</span>
                            )}
                        </motion.button>
                    </div>
                </motion.div>
            </motion.div>

            <Footer />
        </div>
    );
};

export default ResultPage;